import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import type { DoclenzAuditService } from '@services/DoclenzAuditService'
import {
  AUDIT_ACTIONS,
  actionLabel,
  outcomeColor,
  type AuditEventDto,
} from '@domain/audit/AuditDto'

export function AuditView() {
  const auditApi = useArchbaseRemoteServiceApi<DoclenzAuditService>(API_TYPE.DoclenzAudit)
  const [events, setEvents] = useState<AuditEventDto[]>([])
  const [action, setAction] = useState<string | null>(null)
  const [tenantId, setTenantId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await auditApi.findAll({
        action: action || undefined,
        tenantId: tenantId.trim() || undefined,
        limit: 200,
      })
      setEvents(response.events || [])
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar a auditoria.')
    } finally {
      setLoading(false)
    }
  }, [auditApi, action, tenantId])

  useEffect(() => {
    load()
  }, [load])

  return (
    /*
      O contêiner de abas do Archbase tem altura definida e não rola: sem isto a lista cresce
      além do fundo da tela e as últimas linhas simplesmente somem, sem barra nenhuma. A rolagem
      tem de ser da view, não da página.

      minHeight 0 não é decoração: num filho de flex o padrão é `auto`, que impede o encolhimento
      e faz o overflow vazar em vez de rolar.
    */
    <Stack
      p="md"
      gap="lg"
      style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}
    >
      <Group justify="space-between">
        <Title order={3}>Auditoria</Title>
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load}>
          Atualizar
        </Button>
      </Group>

      <Alert color="yellow" variant="light">
        O autor é a impressão digital da admin key usada, <b>não uma pessoa</b> — o admin autentica
        por segredo compartilhado e ainda não tem identidade individual. Por isso o IP de origem é o
        único traço que separa dois portadores da mesma chave, e por isso tentativas recusadas são
        registradas: é o principal sinal de que uma chave vazou.
      </Alert>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Group>
        <Select
          placeholder="Todas as ações"
          data={AUDIT_ACTIONS}
          value={action}
          onChange={setAction}
          clearable
          w={260}
        />
        <TextInput
          placeholder="Filtrar por tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.currentTarget.value)}
          w={220}
        />
      </Group>

      {loading ? (
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      ) : events.length === 0 ? (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center">
            Nenhum evento no filtro atual.
          </Text>
        </Card>
      ) : (
        <Card withBorder padding="sm">
          {/* Cinco colunas com valor antigo e novo lado a lado; a mesma armadilha da tela de
              configuracoes, so que pior. */}
          <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover verticalSpacing="xs" fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={150}>Quando</Table.Th>
                <Table.Th w={190}>Ação</Table.Th>
                <Table.Th>Alvo</Table.Th>
                <Table.Th>Mudança</Table.Th>
                <Table.Th w={210}>Autor</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.map((event) => (
                <Table.Tr key={event.id}>
                  <Table.Td>
                    <Text size="xs">{formatInstant(event.at)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={outcomeColor(event.outcome)} variant="light" size="sm">
                      {actionLabel(event.action)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" lineClamp={1}>
                      {event.target || '—'}
                    </Text>
                    {event.tenant_id && (
                      <Text size="xs" c="dimmed">
                        tenant: {event.tenant_id}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {event.field ? (
                      <Stack gap={0}>
                        <Text size="xs" fw={500}>
                          {event.field}
                        </Text>
                        <Text size="xs" c="dimmed">
                          <Text component="span" td="line-through">
                            {event.old_value ?? '—'}
                          </Text>{' '}
                          → {event.new_value ?? '—'}
                        </Text>
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed">
                        {event.new_value ?? '—'}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" lineClamp={1}>
                      {event.actor}
                    </Text>
                    {event.source_ip && (
                      <Text size="xs" c="dimmed">
                        {event.source_ip}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </Stack>
  )
}

function formatInstant(value: string): string {
  try {
    return new Date(value).toLocaleString('pt-BR')
  } catch {
    return value
  }
}
